import { Contract, SetAsideType } from '../../types';
import { Profile } from '../../contexts/AuthContext';
import { KeywordExtractor } from './KeywordExtractor';

export interface ScoreBreakdown {
  category: string;
  score: number;
  weight: number;
  description: string;
  icon?: string;
}

export interface DetailedScore {
  totalScore: number;
  breakdown: ScoreBreakdown[];
  recommendation: string;
  strengthAreas: string[];
  improvementAreas: string[];
}

// Enhanced score with requirements analysis
export interface EnhancedDetailedScore extends DetailedScore {
  requirementsAnalysis?: {
    totalRequirements: number;
    mandatoryMet: number;
    mandatoryTotal: number;
    capabilityGaps: string[];
    strongMatches: string[];
  };
}

// Contract requirement interface for scoring
export interface ContractRequirement {
  requirement_type: string;
  category?: string;
  requirement_text: string;
  is_mandatory: boolean;
  priority: string;
  keywords?: string[];
}

export class ContractMatcher {
  // Adjusted Weights for BidWin Score Calculation
  static readonly WEIGHTS = {
    naicsMatch: 0.27,            // NAICS Code Match (27%)
    setAsideMatch: 0.27,         // Set-Aside Match (27%)
    capabilities: 0.21,          // Capabilities Match (21%)
    pricingDataAvailability: 0.05, // Pricing Data Availability (5%)
    agencyHistory: 0.20          // Agency/Contracting History (20%)
  };

  // Score thresholds - consistent across all components
  static readonly THRESHOLDS = {
    strong: 70,    // 70%+ is strong match
    moderate: 50,  // 50-69% is moderate match
    weak: 0        // Below 50% is weak match
  };

  // Hard ceiling applied to the score when the company is INELIGIBLE for the
  // solicitation's set-aside. Eligibility is binary in federal contracting — you
  // either hold the required certification or you legally cannot win — so it must
  // gate the score, not just shave a weighted slice off it. An ineligible contract
  // can never rank above "weak", regardless of how well NAICS/capabilities align.
  static readonly INELIGIBLE_SCORE_CEILING = 15;

  /**
   * Deterministic set-aside eligibility verdict, shared vocabulary with the AI
   * capture analyst (ai-match-contracts): 'eligible' | 'ineligible'.
   * - No set-aside on the notice  -> eligible (full & open competition).
   * - Company holds one of the required set-asides -> eligible.
   * - Notice is set aside for a certification the company does NOT hold -> ineligible.
   */
  static getEligibility(
    contractSetAsides: SetAsideType[] | undefined,
    profileSetAsides: SetAsideType[] | undefined,
  ): 'eligible' | 'ineligible' {
    const required = contractSetAsides || [];
    if (required.length === 0) return 'eligible'; // open competition
    const held = new Set(profileSetAsides || []);
    return required.some((sa) => held.has(sa)) ? 'eligible' : 'ineligible';
  }

  // Map a 0-100 score + eligibility into the AI's tier vocabulary so both scoring
  // systems speak one language across the UI (strong | possible | weak | ineligible).
  static getTier(score: number, eligibility: 'eligible' | 'ineligible'): 'strong' | 'possible' | 'weak' | 'ineligible' {
    if (eligibility === 'ineligible') return 'ineligible';
    if (score >= this.THRESHOLDS.strong) return 'strong';
    if (score >= this.THRESHOLDS.moderate) return 'possible';
    return 'weak';
  }

  static calculateMatchScore(contract: Contract, profile: Profile | null): number {
    if (!profile) return 0; // No score for unauthenticated users

    const scores = {
      naicsMatch: this.calculateNaicsMatch(contract.naicsCode, profile.naics_codes || []),
      setAsideMatch: this.calculateSetAsideMatch(contract.setAsides || [], profile.qualified_set_asides || []),
      capabilities: this.calculateCapabilitiesMatch(contract, profile),
      pricingDataAvailability: this.calculatePricingDataAvailability(contract.naicsCode, profile.pricingData),
      agencyHistory: this.calculateAgencyMatch(contract.agency, profile.pastPerformance || [])
    };

    // Calculate weighted total score
    const totalScore =
      (scores.naicsMatch * this.WEIGHTS.naicsMatch) +
      (scores.setAsideMatch * this.WEIGHTS.setAsideMatch) +
      (scores.capabilities * this.WEIGHTS.capabilities) +
      (scores.pricingDataAvailability * this.WEIGHTS.pricingDataAvailability) +
      (scores.agencyHistory * this.WEIGHTS.agencyHistory);

    const raw = Math.round(totalScore * 100); // Convert to percentage

    // HARD ELIGIBILITY GATE: if the company can't legally win this set-aside,
    // cap the score so it can never surface as a strong/possible match.
    const eligibility = this.getEligibility(contract.setAsides, profile.qualified_set_asides);
    if (eligibility === 'ineligible') return Math.min(raw, this.INELIGIBLE_SCORE_CEILING);

    return raw;
  }

  // Get detailed score breakdown with recommendations
  static getDetailedScore(contract: Contract, profile: Profile | null): DetailedScore {
    if (!profile) {
      return {
        totalScore: 0,
        breakdown: [],
        recommendation: 'Complete your company profile to see match scores',
        strengthAreas: [],
        improvementAreas: ['Complete company profile']
      };
    }

    const naicsScore = this.calculateNaicsMatch(contract.naicsCode, profile.naics_codes || []);
    const setAsideScore = this.calculateSetAsideMatch(contract.setAsides || [], profile.qualified_set_asides || []);
    const capabilitiesScore = this.calculateCapabilitiesMatch(contract, profile);
    const pricingScore = this.calculatePricingDataAvailability(contract.naicsCode, profile.pricingData);
    const agencyScore = this.calculateAgencyMatch(contract.agency, profile.pastPerformance || []);

    const breakdown: ScoreBreakdown[] = [
      {
        category: 'NAICS Code Match',
        score: Math.round(naicsScore * 100),
        weight: Math.round(this.WEIGHTS.naicsMatch * 100),
        description: this.getNaicsDescription(naicsScore),
        icon: 'tag'
      },
      {
        category: 'Set-Aside Eligibility',
        score: Math.round(setAsideScore * 100),
        weight: Math.round(this.WEIGHTS.setAsideMatch * 100),
        description: this.getSetAsideDescription(setAsideScore, contract.setAsides || []),
        icon: 'award'
      },
      {
        category: 'Capabilities Alignment',
        score: Math.round(capabilitiesScore * 100),
        weight: Math.round(this.WEIGHTS.capabilities * 100),
        description: this.getCapabilitiesDescription(capabilitiesScore),
        icon: 'briefcase'
      },
      {
        category: 'Pricing Data',
        score: Math.round(pricingScore * 100),
        weight: Math.round(this.WEIGHTS.pricingDataAvailability * 100),
        description: pricingScore >= 0.5 ? 'Pricing data available for this NAICS' : 'Add pricing data to improve estimates',
        icon: 'dollar-sign'
      },
      {
        category: 'Agency Experience',
        score: Math.round(agencyScore * 100),
        weight: Math.round(this.WEIGHTS.agencyHistory * 100),
        description: this.getAgencyDescription(agencyScore, contract.agency),
        icon: 'building'
      }
    ];

    const totalScore = this.calculateMatchScore(contract, profile);

    // Identify strengths and improvements
    const strengthAreas: string[] = [];
    const improvementAreas: string[] = [];

    breakdown.forEach(item => {
      if (item.score >= 80) {
        strengthAreas.push(item.category);
      } else if (item.score < 50) {
        improvementAreas.push(item.category);
      }
    });

    // Generate recommendation
    const recommendation = this.generateRecommendation(totalScore, breakdown, contract);

    return {
      totalScore,
      breakdown,
      recommendation,
      strengthAreas,
      improvementAreas
    };
  }

  private static getNaicsDescription(score: number): string {
    if (score >= 1.0) return 'Exact NAICS code match - Strong alignment';
    if (score >= 0.8) return 'Related NAICS code (4-digit match)';
    if (score >= 0.4) return 'Same industry sector (2-digit match)';
    return 'Add this NAICS code to your profile';
  }

  private static getSetAsideDescription(score: number, setAsides: SetAsideType[]): string {
    if (!setAsides.length) return 'Open competition - No set-aside requirements';
    if (score >= 1.0) return `Qualified for all set-asides: ${setAsides.join(', ')}`;
    if (score >= 0.5) return `Partially qualified for: ${setAsides.join(', ')}`;
    return `Not qualified for: ${setAsides.join(', ')}. Update your certifications.`;
  }

  private static getCapabilitiesDescription(score: number): string {
    if (score >= 0.8) return 'Strong capability alignment with requirements';
    if (score >= 0.6) return 'Good capability match - Minor gaps';
    if (score >= 0.4) return 'Moderate capability alignment';
    return 'Limited capability match - Review requirements carefully';
  }

  // Turn SAM's all-caps agency strings into a readable name for prose, e.g.
  // "VETERANS AFFAIRS, DEPARTMENT OF" -> "the Department of Veterans Affairs".
  private static humanizeAgency(agency?: string): string {
    const raw = (agency || '').trim();
    if (!raw) return 'this agency';
    const titleCase = (s: string) =>
      s.toLowerCase().replace(/\b([a-z])/g, (m) => m.toUpperCase()).trim();
    // Handle the common "<NAME>, DEPARTMENT OF" / "<NAME>, OFFICE OF" inversions.
    const inv = raw.match(/^(.*),\s*(DEPARTMENT|OFFICE|BUREAU|AGENCY)\s+OF\s*$/i);
    if (inv) return `the ${titleCase(inv[2])} of ${titleCase(inv[1])}`;
    return titleCase(raw);
  }

  private static getAgencyDescription(score: number, agency: string): string {
    const name = this.humanizeAgency(agency);
    if (score >= 1.0) return `Past performance with ${name}`;
    if (score >= 0.8) return `Past performance with related ${name.replace(/^the /, '')} components`;
    if (score >= 0.6) return `Related agency experience — transferable to ${name}`;
    // We can't actually verify the absence of past performance from the profile
    // data here, so don't assert a false negative ("No prior X experience").
    // Frame it as a new relationship and a constructive next step instead.
    return `New relationship with ${name} — highlight transferable experience`;
  }

  private static generateRecommendation(totalScore: number, breakdown: ScoreBreakdown[], contract: Contract): string {
    if (totalScore >= 80) {
      return 'Excellent match! This opportunity aligns well with your capabilities. Consider prioritizing this bid.';
    }
    if (totalScore >= 70) {
      return 'Strong match. Review the requirements carefully and emphasize your relevant experience.';
    }
    if (totalScore >= 50) {
      const weakest = breakdown.reduce((min, b) => b.score < min.score ? b : min);
      return `Moderate match. Focus on strengthening your ${weakest.category.toLowerCase()} to improve your competitive position.`;
    }
    return 'This opportunity has limited alignment with your profile. Consider whether the effort to bid is worthwhile.';
  }

  // NAICS Code Match (Exact = 100, Partial (4-digit) = 80, Industry (2-digit) = 40, No Match = 10)
  // Made public for use in ContractDetails score breakdown
  static calculateNaicsMatch(contractNaics: string, profileNaics: string[]): number {
    if (!contractNaics || !profileNaics?.length) return 0.1;

    if (profileNaics.includes(contractNaics)) return 1.0;
    if (profileNaics.some(code => code.slice(0, 4) === contractNaics.slice(0, 4))) return 0.8;
    if (profileNaics.some(code => code.slice(0, 2) === contractNaics.slice(0, 2))) return 0.4;

    return 0.1;
  }

  // Set-Aside Match (Full = 100, Partial = 50, No Match = 10)
  static calculateSetAsideMatch(contractSetAsides: SetAsideType[], profileSetAsides: SetAsideType[]): number {
    if (!contractSetAsides?.length) return 1.0;
    if (!profileSetAsides?.length) return 0.1;

    const matchingSetAsides = contractSetAsides.filter(setAside => profileSetAsides.includes(setAside));
    return matchingSetAsides.length === 0 ? 0.1 : matchingSetAsides.length === contractSetAsides.length ? 1.0 : 0.5;
  }

  // Capabilities Match (Using Keyword Overlap)
  static calculateCapabilitiesMatch(contract: Contract, profile: Profile): number {
    if (!contract.description || !profile) return 0.1;

    // Extract keywords from contract description and title
    const contractKeywords = [
      ...KeywordExtractor.extractKeywords(contract.title),
      ...KeywordExtractor.extractKeywords(contract.description)
    ];

    // Combine all profile capabilities
    const profileKeywords = [
      ...(profile.core_competencies || []),
      ...(profile.key_technologies || []),
      ...(profile.industries_served || []),
      profile.value_proposition || ''
    ].filter(Boolean);

    if (profileKeywords.length === 0) return 0.1;

    // Calculate similarity score
    const similarity = KeywordExtractor.calculateSimilarity(contractKeywords, profileKeywords);

    // Apply a more granular scoring scale
    if (similarity >= 0.8) return 1.0;  // Excellent match
    if (similarity >= 0.6) return 0.8;  // Strong match
    if (similarity >= 0.4) return 0.6;  // Moderate match
    if (similarity >= 0.2) return 0.4;  // Weak match
    return 0.1;                         // Poor match
  }

  // Pricing Data Availability (Only Checks If Data Exists)
  static calculatePricingDataAvailability(contractNaics: string, pricingData: any): number {
    if (!pricingData) return 0.1;

    const hasPricingForNAICS = pricingData.pricingRules?.some((rule: any) =>
      rule.naicsCodes.includes(contractNaics)
    );

    return hasPricingForNAICS ? 1.0 : 0.1;
  }

  // Agency/Contracting History (Checks if Company Worked with the Agency Before)
  // Common federal agency acronyms -> their significant name words, so "VA"
  // matches "Department of Veterans Affairs" and "DoD" matches "Department of
  // Defense". Keys are lowercase; values expand to whitespace-joined tokens.
  private static readonly AGENCY_ALIASES: Record<string, string> = {
    va: 'veterans affairs',
    dod: 'defense',
    dhs: 'homeland security',
    gsa: 'general services',
    hhs: 'health human',
    doj: 'justice',
    dot: 'transportation',
    hud: 'housing urban development',
    usda: 'agriculture',
    nasa: 'aeronautics space',
    epa: 'environmental protection',
    ssa: 'social security',
    irs: 'internal revenue',
    cdc: 'disease control prevention',
    nih: 'health',
    fbi: 'investigation',
    tsa: 'transportation security',
    cbp: 'customs border protection',
    fema: 'emergency management',
    navy: 'navy',
    army: 'army',
    af: 'air force',
    usaf: 'air force',
  };

  // Words that carry no discriminating signal in an agency name. Dropping them
  // lets word-order/punctuation variants reconcile: "VETERANS AFFAIRS,
  // DEPARTMENT OF" and "Department of Veterans Affairs" both reduce to
  // {veterans, affairs}.
  private static readonly AGENCY_FILLER = new Set([
    'department', 'dept', 'of', 'the', 'us', 'u', 's', 'usa', 'office', 'bureau',
    'agency', 'administration', 'for', 'and', 'services', 'service', 'national',
    'federal', 'division', 'directorate',
  ]);

  // Reduce an agency string to its set of significant, acronym-expanded tokens.
  private static agencyTokens(raw?: string): Set<string> {
    if (!raw) return new Set();
    const words = String(raw).toLowerCase().replace(/[^a-z0-9 ]/g, ' ').split(/\s+/).filter(Boolean);
    const out: string[] = [];
    for (const w of words) {
      const alias = this.AGENCY_ALIASES[w];
      if (alias) out.push(...alias.split(' '));
      else out.push(w);
    }
    return new Set(out.filter((t) => t && !this.AGENCY_FILLER.has(t)));
  }

  // Score how well a contract's agency matches the company's past-performance
  // agencies. Compares on significant tokens (not raw substrings), so SAM's
  // "VETERANS AFFAIRS, DEPARTMENT OF" reconciles with a stored "Department of
  // Veterans Affairs" or a "va" department code. Checks every agency field on
  // each record and returns the best match found.
  static calculateAgencyMatch(contractAgency: string, pastPerformance: any[]): number {
    if (!contractAgency || !pastPerformance?.length) return 0.1;

    const cTokens = this.agencyTokens(contractAgency);
    if (cTokens.size === 0) return 0.1;

    let best = 0.1;
    for (const record of pastPerformance) {
      const candidates = [
        record?.agency,
        record?.sub_agency,
        record?.agency_selection?.fullPath,
        record?.agency_selection?.agency,
        record?.agency_selection?.department,
      ];
      for (const cand of candidates) {
        const rTokens = this.agencyTokens(cand);
        if (rTokens.size === 0) continue;

        const overlap = [...cTokens].filter((t) => rTokens.has(t)).length;
        if (overlap === 0) continue;

        const smaller = Math.min(cTokens.size, rTokens.size);
        if (overlap === cTokens.size && overlap === rTokens.size) {
          return 1.0; // identical significant-token sets — same agency
        }
        if (overlap === smaller) {
          best = Math.max(best, 0.8); // one name fully contains the other
        } else if (overlap / smaller >= 0.5) {
          best = Math.max(best, 0.6); // substantial shared signal
        }
      }
    }

    return best;
  }

  // BidWin Score Labeling and Color Codes - Using consistent THRESHOLDS
  static getWinScoreColor(score: number): string {
    if (score >= this.THRESHOLDS.strong) return 'text-green-600';
    if (score >= this.THRESHOLDS.moderate) return 'text-yellow-600';
    return 'text-red-600';
  }

  static getWinScoreBackground(score: number): string {
    if (score >= this.THRESHOLDS.strong) return 'bg-green-100';
    if (score >= this.THRESHOLDS.moderate) return 'bg-yellow-100';
    return 'bg-red-100';
  }

  static getWinScoreBorderColor(score: number): string {
    if (score >= this.THRESHOLDS.strong) return 'border-green-400';
    if (score >= this.THRESHOLDS.moderate) return 'border-yellow-400';
    return 'border-red-400';
  }

  static getWinScoreGradient(score: number): string {
    if (score >= this.THRESHOLDS.strong) return 'from-green-500 to-emerald-600';
    if (score >= this.THRESHOLDS.moderate) return 'from-yellow-500 to-amber-600';
    return 'from-red-500 to-rose-600';
  }

  static getWinScoreLabel(score: number): string {
    if (score >= this.THRESHOLDS.strong) return 'Strong Match';
    if (score >= this.THRESHOLDS.moderate) return 'Moderate Match';
    return 'Weak Match';
  }

  static getWinScoreEmoji(score: number): string {
    if (score >= this.THRESHOLDS.strong) return '🎯';
    if (score >= this.THRESHOLDS.moderate) return '📊';
    return '⚠️';
  }

  // Get a brief explanation of what the score means
  static getScoreExplanation(score: number): string {
    if (score >= 90) return 'Exceptional opportunity alignment';
    if (score >= 80) return 'Excellent match for your capabilities';
    if (score >= 70) return 'Strong potential - prioritize this bid';
    if (score >= 60) return 'Good match with minor gaps';
    if (score >= 50) return 'Moderate alignment - review carefully';
    if (score >= 40) return 'Limited match - consider effort vs reward';
    if (score >= 30) return 'Weak alignment with your profile';
    return 'Poor match - not recommended';
  }

  // ============================================================
  // ENHANCED SCORING WITH EXTRACTED REQUIREMENTS
  // ============================================================

  /**
   * Calculate enhanced BidWin Score using extracted requirements
   * This provides more accurate scoring by analyzing actual RFP requirements
   */
  static calculateEnhancedScore(
    contract: Contract,
    profile: Profile | null,
    requirements: ContractRequirement[]
  ): number {
    if (!profile) return 0;

    // If no requirements extracted, fall back to standard scoring
    if (!requirements || requirements.length === 0) {
      return this.calculateMatchScore(contract, profile);
    }

    // Calculate base score
    const baseScore = this.calculateMatchScore(contract, profile);

    // Calculate requirements-based score adjustment
    const reqAnalysis = this.analyzeRequirementsMatch(requirements, profile);

    // Weight: 70% base score, 30% requirements analysis
    const enhancedScore = (baseScore * 0.7) + (reqAnalysis.score * 0.3);

    return Math.round(enhancedScore);
  }

  /**
   * Analyze how well profile matches extracted requirements
   */
  static analyzeRequirementsMatch(
    requirements: ContractRequirement[],
    profile: Profile
  ): { score: number; gaps: string[]; matches: string[]; mandatoryMet: number; mandatoryTotal: number } {
    const gaps: string[] = [];
    const matches: string[] = [];
    let mandatoryMet = 0;
    let mandatoryTotal = 0;
    let totalScore = 0;
    let totalWeight = 0;

    // Combine all profile keywords/capabilities
    const profileCapabilities = [
      ...(profile.core_competencies || []),
      ...(profile.key_technologies || []),
      ...(profile.industries_served || []),
      ...(profile.certifications || []),
      profile.value_proposition || ''
    ].filter(Boolean).map(s => s.toLowerCase());

    // Priority weights
    const priorityWeights: Record<string, number> = {
      critical: 4,
      high: 3,
      medium: 2,
      low: 1
    };

    for (const req of requirements) {
      const weight = priorityWeights[req.priority] || 2;
      totalWeight += weight;

      if (req.is_mandatory) {
        mandatoryTotal++;
      }

      // Check if profile matches this requirement
      const reqKeywords = req.keywords || [];
      const reqText = req.requirement_text.toLowerCase();

      // Calculate match for this requirement
      let reqMatch = 0;

      // Check keyword matches
      if (reqKeywords.length > 0) {
        const matchedKeywords = reqKeywords.filter(kw =>
          profileCapabilities.some(cap =>
            cap.includes(kw.toLowerCase()) || kw.toLowerCase().includes(cap)
          )
        );
        reqMatch = matchedKeywords.length / reqKeywords.length;
      } else {
        // Fall back to text similarity
        const textWords = reqText.split(/\s+/).filter(w => w.length > 3);
        const matchedWords = textWords.filter(word =>
          profileCapabilities.some(cap => cap.includes(word))
        );
        reqMatch = textWords.length > 0 ? matchedWords.length / textWords.length : 0;
      }

      // Score this requirement
      if (reqMatch >= 0.5) {
        totalScore += weight;
        if (req.is_mandatory) mandatoryMet++;
        if (reqMatch >= 0.7) {
          matches.push(req.category || req.requirement_type);
        }
      } else if (req.is_mandatory || req.priority === 'critical' || req.priority === 'high') {
        gaps.push(req.category || req.requirement_type);
      }
    }

    // Calculate final score (0-100)
    const score = totalWeight > 0
      ? Math.round((totalScore / totalWeight) * 100)
      : 50; // Default to 50 if no requirements

    return {
      score,
      gaps: [...new Set(gaps)].slice(0, 5), // Unique, max 5
      matches: [...new Set(matches)].slice(0, 5),
      mandatoryMet,
      mandatoryTotal
    };
  }

  /**
   * Get enhanced detailed score with requirements analysis
   */
  static getEnhancedDetailedScore(
    contract: Contract,
    profile: Profile | null,
    requirements: ContractRequirement[]
  ): EnhancedDetailedScore {
    // Get base detailed score
    const baseDetail = this.getDetailedScore(contract, profile);

    if (!profile || !requirements || requirements.length === 0) {
      return baseDetail;
    }

    // Analyze requirements
    const reqAnalysis = this.analyzeRequirementsMatch(requirements, profile);

    // Add requirements breakdown
    const enhancedBreakdown = [...baseDetail.breakdown];

    // Add requirements analysis as a new category
    enhancedBreakdown.push({
      category: 'Requirements Match',
      score: reqAnalysis.score,
      weight: 30, // 30% weight when requirements are available
      description: this.getRequirementsDescription(reqAnalysis),
      icon: 'clipboard-check'
    });

    // Recalculate total with requirements weighting
    const enhancedTotal = this.calculateEnhancedScore(contract, profile, requirements);

    // Add gaps to improvement areas
    const improvementAreas = [...baseDetail.improvementAreas];
    if (reqAnalysis.gaps.length > 0) {
      improvementAreas.push(`Requirements gaps: ${reqAnalysis.gaps.join(', ')}`);
    }

    // Add matches to strength areas
    const strengthAreas = [...baseDetail.strengthAreas];
    if (reqAnalysis.matches.length > 0 && reqAnalysis.score >= 70) {
      strengthAreas.push(`Strong in: ${reqAnalysis.matches.join(', ')}`);
    }

    return {
      totalScore: enhancedTotal,
      breakdown: enhancedBreakdown,
      recommendation: this.getEnhancedRecommendation(enhancedTotal, reqAnalysis),
      strengthAreas,
      improvementAreas,
      requirementsAnalysis: {
        totalRequirements: requirements.length,
        mandatoryMet: reqAnalysis.mandatoryMet,
        mandatoryTotal: reqAnalysis.mandatoryTotal,
        capabilityGaps: reqAnalysis.gaps,
        strongMatches: reqAnalysis.matches
      }
    };
  }

  private static getRequirementsDescription(analysis: { score: number; mandatoryMet: number; mandatoryTotal: number }): string {
    const mandatory = analysis.mandatoryTotal > 0
      ? ` (${analysis.mandatoryMet}/${analysis.mandatoryTotal} mandatory met)`
      : '';

    if (analysis.score >= 80) return `Excellent requirements alignment${mandatory}`;
    if (analysis.score >= 60) return `Good requirements match${mandatory}`;
    if (analysis.score >= 40) return `Moderate requirements alignment${mandatory}`;
    return `Review requirements carefully${mandatory}`;
  }

  private static getEnhancedRecommendation(score: number, reqAnalysis: { mandatoryMet: number; mandatoryTotal: number; gaps: string[] }): string {
    // Check mandatory requirements
    if (reqAnalysis.mandatoryTotal > 0 && reqAnalysis.mandatoryMet < reqAnalysis.mandatoryTotal) {
      const unmet = reqAnalysis.mandatoryTotal - reqAnalysis.mandatoryMet;
      return `Warning: ${unmet} mandatory requirement(s) not fully met. Review carefully before bidding.`;
    }

    if (score >= 80) {
      return 'Excellent opportunity! Your capabilities align well with the extracted requirements.';
    }
    if (score >= 70) {
      return 'Strong match based on requirements analysis. Prioritize this opportunity.';
    }
    if (score >= 50) {
      const gapText = reqAnalysis.gaps.length > 0
        ? ` Address gaps in: ${reqAnalysis.gaps.slice(0, 3).join(', ')}.`
        : '';
      return `Moderate alignment.${gapText} Consider teaming arrangements if needed.`;
    }
    return 'Limited alignment with requirements. Evaluate effort vs. win probability carefully.';
  }
}